import React, { useState, useRef } from 'react';
import { Button, Dropdown, Form } from "react-bootstrap";
import CallForwardBarChart from '../ChartComponents/CallForwardBarChart';
import OutgoingCallsBarChart from '../ChartComponents/OutgoingCallsBarChart';
import AgentWiseTotalCallsChart from '../ChartComponents/AgentWiseTotalCallsChart';
import MissedCallPieChart from '../ChartComponents/MissedCallPieChart';
import IncomingCallBreakdownChart from '../ChartComponents/IncomingCallBreakdownChart';
import AverageCallLengthChart from '../ChartComponents/AverageCallLengthChart';
import HeatMapChart from '../ChartComponents/HeatMapChart';
import AgentWiseAvgCallLength from '../ChartComponents/AgentWiseAvgCallLength';
import MissedCallNoAgentChart from '../ChartComponents/MissedCallNoAgentChart';
import VoiceMailsLineChart from '../ChartComponents/VoiceMailsLineChart';
import AgentActivityChart from '../ChartComponents/AgentActivityChart';
import DatePicker from 'react-datepicker';
import { GetApiMethod } from '../../core/services/apiService';
import { Container } from "react-bootstrap";
import TopHeader from "../Common/TopHeader";
import "./../ChartComponents/apexChart.css";
import 'react-datepicker/dist/react-datepicker.css';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const DashboardReports = () => {
  const callForwardAndOutGoingCalls_apiEndpoint = 'admin/dashboard-chart/call_forwarded_analytics_week';
  const agentWiseTotalCalls_apiEndpoint = 'admin/dashboard-chart/agentWise-totalCalls';
  const missedCall_apiEndPoint = "admin/dashboard-chart/missed_call_reports";
  const incomingCallBreakdown_apiEndPoint = "admin/dashboard-chart/incoming_call_breakdown_reports";
  const averageCallLength_apiEndPoint = "admin/dashboard-chart/average_call_length_reports";
  const heatMap_apiEndpoint = 'admin/dashboard-chart/heatmap_analytics';
  const agentWiseAvgCallLength_apiEndpoint = 'admin/dashboard-chart/agentwise_avg_all_length_reports';
  const missedCall_noAgent_apiEndpoint = "admin/dashboard-chart/missedCall_noAgents_reports";
  const voiceMails_apiEndpoint = "admin/dashboard-chart/voice_mails_analytics";
  const agentActivity_apiEndpoint = "admin/dashboard-chart/agent_activity_reports";
  const oneMonthBefore = new Date();
  oneMonthBefore.setMonth(oneMonthBefore.getMonth() - 1);
  const [startDate, setStartDate] = useState<Date | null>(oneMonthBefore);
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [agentList, setAgentList] = useState<any[]>([]);
  const callTypeOptionsList = [{ id: "all", name: "All" }, { id: "voice", name: "Voice" }, { id: "video", name: "Video" }];
  const [locationList, setLocationList] = useState<any[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<string[]>([]);
  const [selectedCallType, setSelectedCallType] = useState('all');
  const [selectedCallDirection, setSelectedCallDirection] = useState("all");
  const [locationSearchText, setLocationSearchText] = useState("");
  const [agentSearchText, setAgentSearchText] = useState("");
  const divRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);

  const totalCharts = 14;
  const [loadedCharts, setLoadedCharts] = useState(0);
  const handleChartLoaded = () => { setLoadedCharts((prev) => prev + 1) };
  const allChartsLoaded = loadedCharts === totalCharts;
  const isLoading = loading || !allChartsLoaded;

  const [isStartCalendarOpen, setIsStartCalendarOpen] = useState(false);
  const [isEndCalendarOpen, setIsEndCalendarOpen] = useState(false);

  const handleStartCalendarOpen = () => {
    setIsStartCalendarOpen(true);
  };

  const handleStartCalendarClose = () => {
    setIsStartCalendarOpen(false);
  };

  const handleEndCalendarOpen = () => {
    setIsEndCalendarOpen(true);
  };

  const handleEndCalendarClose = () => {
    setIsEndCalendarOpen(false);
  };
  const handleStartDateChange = (date: Date | null) => {

    console.log(date, 'date ==========')

    if (!date || isNaN(date.getTime())) {
      console.warn("Invalid start date entered manually.");
      setStartDate(null);
      return;
    }
    // prevent startDate > endDate
    if (endDate && date > endDate) {
      console.warn("Start date cannot be after end date, adjusting endDate.");
      setEndDate(date);
    }
    setStartDate(date);
  };

  const handleEndDateChange = (date: Date | null) => {
    if (!date || isNaN(date.getTime())) {
      console.warn("Invalid end date entered manually.");
      setEndDate(null);
      return;
    }
    // prevent endDate < startDate
    if (startDate && date < startDate) {
      console.warn("End date cannot be before start date, adjusting startDate.");
      setStartDate(date);
    }
    setEndDate(date);
  };

  const getAgentList = React.useCallback(async () => {
    try {
      const res = await GetApiMethod("tenant/agent-crud");
      const transformed = res.data.data.map((agent: any) => ({
        id: agent._id,
        name: agent.name,
      }));
      setAgentList(transformed);
    } catch (err) {
      console.error("Error fetching agent list:", err);
    }
  }, []);


  const getLocationList = React.useCallback(async () => {
    GetApiMethod("tenant/location-crud").then((res) => {
      const transformed = res.data.data.map((location: any) => ({
        id: location._id,
        name: location.name,
      }));
      setLocationList(transformed);
    });
  }, []);


  React.useEffect(() => {
    getAgentList()
    getLocationList()
  }, []);

  const downloadPdf = () => {
    setLoading(true)
    const content = document.getElementById('chart-download');
    if (content) {

      const input = divRef.current;
      if (!input) return;
      html2canvas(input, { scale: 1.5, useCORS: true, logging: false, }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "pt", [595.28, 1000]);
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        pdf.save("dashboard-reports.pdf");
        setLoading(false);
      });
    }
  };


  const agentPlaceholder = agentList?.length > 0 ? (agentList.filter((opt) =>
    selectedAgent.includes(opt.id)).map((opt) => opt.name).join(", ") || "Select") : "Select";
  const locationPlaceholder = locationList?.length > 0 ? (locationList.filter((opt) =>
    selectedLocation.includes(opt.id)).map((opt) => opt.name).join(", ") || "Select") : "Select";

  const handleAgentOnChange = (id: string) => {
    if (selectedAgent.includes(id)) {
      setSelectedAgent(selectedAgent.filter((item) => item !== id));
    } else {
      setSelectedAgent([...selectedAgent, id]);
    }
  };


  const handleLocationOnChange = (id: string) => {
    if (selectedLocation.includes(id)) {
      setSelectedLocation(selectedLocation.filter((item) => item !== id));
    } else {
      setSelectedLocation([...selectedLocation, id]);
    }
  };

  const getFilteredLocations = () => {
    const list = Array.isArray(locationList) ? locationList : [];
    if (!locationSearchText) return list;
    return list.filter((option) =>
      option?.name?.toLowerCase()?.includes(locationSearchText.toLowerCase())
    );
  };

  const getFilteredAgents = () => {
    const list = Array.isArray(agentList) ? agentList : [];
    if (!agentSearchText) return list;
    return list.filter((option) =>
      option?.name?.toLowerCase()?.includes(agentSearchText.toLowerCase())
    );
  };

  return (
    <Container fluid className='px-4 pb-5' >
      <TopHeader heading="Dashboard Analytics" to="dashboard" />
      {isLoading && <div className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center bg-dark bg-opacity-50" style={{ zIndex: 1050 }}>
        <div className="spinner-border text-light" role="status" style={{ width: "4rem", height: "4rem" }}>
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="text-white mt-3 fs-5">Loading...</p>
      </div>
      }
      <div className='d-flex flex-row justify-content-end '>
        <Button className="mb-3 buttons" onClick={(e: any) => { downloadPdf() }} style={{ background: '#0187c4' }} >Export</Button>
      </div>
      <div className="d-flex flex-wrap gap-2 p-3 filter-section">
        <div className="col-sm-6 col-md-4 row mt-2">
          <div className="col-sm-4 col-md-4 m-1">
            <label>From Date</label>
          </div>
          <div className="col-sm-7 col-md-7">
            <DatePicker
              showIcon
              id="fromDateicker"
              selected={startDate}
              className="form-control"
              onChange={handleStartDateChange}
              onCalendarOpen={handleStartCalendarOpen}
              onCalendarClose={handleStartCalendarClose}
              strictParsing={true}
              onChangeRaw={(e: any) => e.preventDefault()}
              dateFormat="yyyy-MM-dd"
              placeholderText="Select start date"
              maxDate={endDate}
              minDate={new Date(2020, 0, 1)}
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              autoComplete="off"
              key={`start-${isStartCalendarOpen}`}
            />
          </div>
        </div>
        <div className="col-sm-6 col-md-4 row mt-2">
          <div className="col-sm-4 col-md-4 m-1">
            <label>End Date</label>
          </div>
          <div className="col-sm-7 col-md-7">
            <DatePicker
              selected={endDate}
              id="toDatePicker"
              className="form-control"
              onChange={handleEndDateChange}
              onCalendarOpen={handleEndCalendarOpen}
              onCalendarClose={handleEndCalendarClose}
              strictParsing={true}
              onChangeRaw={(e: any) => e.preventDefault()}
              dateFormat="yyyy-MM-dd"
              placeholderText="Select end date"
              minDate={startDate}
              maxDate={new Date()}
              showYearDropdown
              showMonthDropdown
              dropdownMode="select"
              autoComplete="off"
              key={`end-${isEndCalendarOpen}`}
            />
          </div>
        </div>

        <div className="col-sm-6 col-md-4 row mt-2">
          <div className="col-sm-4 col-md-4 m-1">
            <label>Location</label>
          </div>
          <div className="col-sm-7 col-md-7">
            <Dropdown className='bg-white w-full custom-multi-select'>
              <Dropdown.Toggle
                variant="primary"
                id="dropdown-basic"
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: '100%',
                  backgroundColor: '#fff',
                  borderColor: '#ced4da',
                  color: "#000",
                  textAlign: 'left',
                  paddingRight: '30px'
                }}
                title={locationPlaceholder}>
                {locationPlaceholder}
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ maxHeight: "250px", overflowY: "auto" }}>
                <div className="px-2 pb-2">
                  <Form.Control
                    type="text"
                    placeholder="Search location..."
                    value={locationSearchText}
                    onChange={(e) => setLocationSearchText(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                {getFilteredLocations().length > 0 ? (
                  getFilteredLocations().map((option) => (
                    <Dropdown.Item
                      as="button"
                      key={option?.id}
                      onClick={() => handleLocationOnChange(option?.id)}
                    >
                      <Form.Check
                        type="checkbox"
                        label={option?.name}
                        checked={selectedLocation.includes(option?.id)}
                        readOnly
                      />
                    </Dropdown.Item>
                  ))
                ) : (
                  <Dropdown.Item disabled>No locations found</Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <div className="col-sm-6 col-md-4 row mt-2">
          <div className="col-sm-4 col-md-4 m-1">
            <label>Agent</label>
          </div>
          <div className="col-sm-7 col-md-7">
            <Dropdown className='bg-white w-full custom-multi-select'>
              <Dropdown.Toggle
                variant="primary"
                id="dropdown-basic"
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: '100%',
                  backgroundColor: '#fff',
                  borderColor: '#ced4da',
                  color: "#000",
                  textAlign: 'left',
                  paddingRight: '30px'
                }}
                title={agentPlaceholder}
              >
                {agentPlaceholder}
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ maxHeight: "250px", overflowY: "auto" }}>
                <div className="px-2 pb-2">
                  <Form.Control
                    type="text"
                    placeholder="Search agent..."
                    value={agentSearchText}
                    onChange={(e) => setAgentSearchText(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                {getFilteredAgents().length > 0 ? (
                  getFilteredAgents().map((option) => (
                    <Dropdown.Item
                      as="button"
                      key={option?.id}
                      onClick={() => handleAgentOnChange(option?.id)}
                    >
                      <Form.Check
                        type="checkbox"
                        label={option?.name}
                        checked={selectedAgent.includes(option?.id)}
                        readOnly
                      />
                    </Dropdown.Item>
                  ))
                ) : (
                  <Dropdown.Item disabled>No agents found</Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <div className="col-sm-6 col-md-4 row mt-2">
          <div className="col-sm-4 col-md-4 m-1">
            <label>Call Type</label>
          </div>
          <div className="col-sm-7 col-md-7">
            <div className="custom-select-wrapper">
              <select
                className="form-select custom-select-icon pe-5"
                id="callTypeDropdown"
                value={selectedCallType}
                onChange={(e) => setSelectedCallType(e.target.value)}
              >
                {callTypeOptionsList?.map((typeData: any) => (
                  <option key={typeData.id} value={typeData.id}>
                    {typeData.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="col-sm-6 col-md-4 row mt-2">
          <div className="col-sm-4 col-md-4 m-1">
            <label>Call Direction</label>
          </div>
          <div className="col-sm-7 col-md-7">
            <select
              id="CallDirectionDropdown"
              value={selectedCallDirection}
              className="form-select custom-select-icon pe-5"
              onChange={(e) => setSelectedCallDirection(e.target.value)}>
              <option value="all">All</option>
              <option value="outgoing call">Outgoing Call</option>
              <option value="incoming call">Incoming Call</option>
            </select>
          </div>
        </div>

      </div>

      <div id="chart-download" ref={divRef}>
        <div className="row mt-5">
          <div className="col-md-6 d-flex">
            <div className="card w-100 h-100 theme-card">
              <MissedCallPieChart apiEndpoint={missedCall_apiEndPoint} startDate={startDate} endDate={endDate} selectedCallType={selectedCallType} selectedLocation={selectedLocation} selectedAgent={selectedAgent} onChartLoaded={handleChartLoaded} />
            </div>
          </div>

          <div className="col-md-6 d-flex">
            <div className="card w-100 h-100 theme-card">
              <IncomingCallBreakdownChart apiEndpoint={incomingCallBreakdown_apiEndPoint} startDate={startDate} endDate={endDate} selectedCallDirection={selectedCallDirection} selectedLocation={selectedLocation} selectedAgent={selectedAgent} selectedCallType={selectedCallType} onChartLoaded={handleChartLoaded} />
            </div>
          </div>
        </div>

        <div className="row mt-5">
          <div className="col-md-6 d-flex">
            <div className="card w-100 h-100 theme-card">
              <OutgoingCallsBarChart apiEndpoint={callForwardAndOutGoingCalls_apiEndpoint} startDate={startDate} endDate={endDate} selectedLocation={selectedLocation} selectedAgent={selectedAgent} selectedCallType={selectedCallType} selectedCallDirection={selectedCallDirection} onChartLoaded={handleChartLoaded} />
            </div>
          </div>

          <div className="col-md-6 d-flex">
            <div className="card w-100 h-100 theme-card">
              <CallForwardBarChart apiEndpoint={callForwardAndOutGoingCalls_apiEndpoint} startDate={startDate} endDate={endDate} selectedLocation={selectedLocation} selectedAgent={selectedAgent} onChartLoaded={handleChartLoaded} />
            </div>
          </div>
        </div>

        <div className="row mt-5">
          <div className="col-md-6 d-flex">
            <div className="card w-100 h-100 theme-card">
              <AverageCallLengthChart apiEndpoint={averageCallLength_apiEndPoint} startDate={startDate} endDate={endDate} selectedLocation={selectedLocation} selectedAgent={selectedAgent} selectedCallDirection={selectedCallDirection} onChartLoaded={handleChartLoaded} />
            </div>
          </div>

          <div className="col-md-6 d-flex">
            <div className="card w-100 h-100 theme-card">
              <AgentWiseTotalCallsChart apiEndpoint={agentWiseTotalCalls_apiEndpoint} startDate={startDate} endDate={endDate} selectedCallType={selectedCallType} selectedLocation={selectedLocation} onChartLoaded={handleChartLoaded} />
            </div>
          </div>
        </div>

        <div className='row mt-5'>
          <div className="col-md-6 d-flex">
            <div className="card w-100 h-100 theme-card">
              <AgentWiseAvgCallLength apiEndpoint={agentWiseAvgCallLength_apiEndpoint} startDate={startDate} endDate={endDate} selectedLocation={selectedLocation} selectedCallType={selectedCallType} onChartLoaded={handleChartLoaded} />
            </div>
          </div>
          <div className="col-md-6 d-flex">
            <div className="card w-100 h-100 theme-card">
              <MissedCallNoAgentChart apiEndpoint={missedCall_noAgent_apiEndpoint} startDate={startDate} endDate={endDate} selectedLocation={selectedLocation} selectedCallType={selectedCallType} onChartLoaded={handleChartLoaded} />
            </div>
          </div>
        </div>

        <div className='row mt-5'>
          <div className="col-md-6 d-flex">
            <div className="card w-100 h-100 theme-card">
              <VoiceMailsLineChart apiEndpoint={voiceMails_apiEndpoint} startDate={startDate} endDate={endDate} selectedLocation={selectedLocation} onChartLoaded={handleChartLoaded} />
            </div>
          </div>
          <div className="col-md-6 d-flex">
            <div className="card w-100 h-100 theme-card">
              <AgentActivityChart apiEndpoint={agentActivity_apiEndpoint} startDate={startDate} endDate={endDate} selectedLocation={selectedLocation} selectedAgent={selectedAgent} onChartLoaded={handleChartLoaded} />
            </div>
          </div>
        </div>

        <div className="row mt-5">
          <div className="col-md-12 d-flex">
            <div className="card w-100 h-100 theme-card">
              <HeatMapChart apiEndpoint={heatMap_apiEndpoint} startDate={startDate} endDate={endDate} selectedLocation={selectedLocation} heatMapType="voicemails" onChartLoaded={handleChartLoaded} />
            </div>
          </div>
        </div>

        <div className="row mt-5">
          <div className="col-md-12 d-flex">
            <div className="card w-100 h-100 theme-card">
              <HeatMapChart apiEndpoint={heatMap_apiEndpoint} startDate={startDate} endDate={endDate} selectedLocation={selectedLocation} selectedAgent={selectedAgent} selectedCallType={selectedCallType} heatMapType="completedCalls" onChartLoaded={handleChartLoaded} />
            </div>
          </div>
        </div>

        <div className="row mt-5">
          <div className="col-md-12 d-flex">
            <div className="card w-100 h-100 theme-card">
              <HeatMapChart apiEndpoint={heatMap_apiEndpoint} startDate={startDate} endDate={endDate} selectedLocation={selectedLocation} selectedAgent={selectedAgent} selectedCallType={selectedCallType} selectedCallDirection={selectedCallDirection} heatMapType="missedCalls" onChartLoaded={handleChartLoaded} />
            </div>
          </div>
        </div>

        <div className="row mt-5">
          <div className="col-md-12 d-flex">
            <div className="card w-100 h-100 theme-card">
              <HeatMapChart apiEndpoint={heatMap_apiEndpoint} startDate={startDate} endDate={endDate} selectedLocation={selectedLocation} heatMapType="forwardedCalls" onChartLoaded={handleChartLoaded} />
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default DashboardReports;